import { createContext, createElement, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useSessionUserId } from "./auth";
import { ApiClient, type ApiClientOptions } from "./http";
import { RealtimeClient } from "./realtime";

export * from "./http";
export * from "./realtime";
export * from "./types";
export * from "./auth";
export * from "./session";
export * from "./app";

interface ApiContextValue {
  api: ApiClient;
  realtime: RealtimeClient;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export interface ApiProviderProps extends ApiClientOptions {
  children: ReactNode;
  queryClient?: QueryClient;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function ApiProvider({ children, queryClient, ...options }: ApiProviderProps) {
  const value = useMemo<ApiContextValue>(
    () => ({
      api: new ApiClient(options),
      realtime: new RealtimeClient({ baseUrl: options.baseUrl, getToken: options.getToken, getFreshToken: options.getFreshToken }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.baseUrl],
  );
  // A new cache per signed-in user: after a sign-out in the same tab the next person must not see the previous one's data, not even for a render.
  // A client passed in by the caller is the caller's to reset.
  const userId = useSessionUserId();
  const qc = useMemo(() => queryClient ?? createQueryClient(), [queryClient, userId]);
  const previous = useRef<QueryClient | null>(null);
  useEffect(() => {
    const replaced = previous.current;
    previous.current = qc;
    if (replaced && replaced !== qc && replaced !== queryClient) replaced.clear();
  }, [qc, queryClient]);
  return createElement(QueryClientProvider, { client: qc }, createElement(ApiContext.Provider, { value }, children));
}

export function useApi(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error("useApi must be used within <ApiProvider>");
  return ctx.api;
}

export function useRealtime(): RealtimeClient {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error("useRealtime must be used within <ApiProvider>");
  return ctx.realtime;
}

export * from "./hooks";
export * from "./permissions";
export * from "./handoff";
export * from "./sso";
export * from "./photo";
export * from "./password";
